/**
 * The note that opens a fight between two animals that could never have met
 * (SPEC Section 8, "cross-era honesty"), in one place for the server's story
 * template and the client's preview, so the two cannot drift — `npm run
 * preview:check` pins them anyway.
 *
 * Two real animals from different eras never met. A storybook animal (era
 * "legend", D-113) never lived at all, so the note says that instead of
 * "different times", which would be a lie about a unicorn. A person on the
 * roster (`person: true`, D-124) is real but is not an animal, so the note
 * says that instead. A hero (`hero: true`, D-168) is the third kind: made up
 * like the unicorn, but a named character in comic books rather than an animal
 * in stories, so "nobody has ever met a real one" would read wrong about him
 * and he gets his own line. A screen character (`character: true`, D-181) is
 * the fourth, and differs from a hero in one word: the page he lives on is a
 * television show. And a legend shares nobody's world, not even
 * another legend's: the unicorn, Brock Lesnar and Batman all carry era
 * "legend", so every caller asks `isCrossEra`, never `a.era !== b.era`, and
 * that pair gets a note too. Two people are the one legend pair that does not
 * (D-174): they are both real and both here now, so there is nothing to explain.
 *
 * Takes anything with `name`, `era`, `person`, `hero`, `character`,
 * `proper_noun` and `pronoun`: a full record or a roster summary.
 */

import { refer, isPerson, isHero, isCharacter } from './names.js'

export const isLegend = (x) => x?.era === 'legend'
/** A legend that lives in stories: not a person on the roster as one, not a hero out of a comic, not a character off a screen. */
export const isStorybook = (x) => isLegend(x) && !isPerson(x) && !isHero(x) && !isCharacter(x)

/**
 * Whether a fight needs the note: different eras, or two legends, whose stories are separate.
 * Two people are the exception (D-174): they carry era "legend" like everyone real-but-not-an-animal,
 * but they share one world and one century, so there is nothing for the note to explain.
 */
export function isCrossEra(a, b) {
  if (isPerson(a) && isPerson(b)) return false
  return a.era !== b.era || (isLegend(a) && isLegend(b))
}

export function eraNote(a, b) {
  if (!isCrossEra(a, b)) return null
  const storybooks = [a, b].filter(isStorybook)
  const persons = [a, b].filter(isPerson)
  const heroes = [a, b].filter(isHero)
  const characters = [a, b].filter(isCharacter)
  if (heroes.length === 2) return 'Neither of these is a real animal — they both live in comic books. But if the two of them squared off...'
  if (heroes.length === 1) {
    const h = refer(heroes[0])
    if (characters.length === 1) {
      const c = refer(characters[0])
      return `${c.Name} lives in a television show and ${h.name} lives in comic books, so nobody has ever met either of them. But if the two of them met...`
    }
    if (persons.length === 1) {
      const p = refer(persons[0])
      return `${h.Name} lives in comic books — nobody has ever met ${h.obj}. ${p.name} is real, but ${p.subj} is a person, not a wild animal. Still, if ${h.subj} stepped off the page to meet ${p.name}...`
    }
    if (storybooks.length === 1) {
      const s = refer(storybooks[0])
      return `${s.Name} lives in stories and ${h.name} lives in comic books, so nobody has ever met either of them. But if the two of them met...`
    }
    return `${h.Name} lives in comic books, not in the wild — nobody has ever met ${h.obj}. But if ${h.subj} stepped off the page today...`
  }
  if (characters.length === 2) return 'Neither of these is a real animal — they both live in television shows. But if the two of them met...'
  if (characters.length === 1) {
    const c = refer(characters[0])
    if (persons.length === 1) {
      const p = refer(persons[0])
      return `${c.Name} lives in a television show — nobody has ever met ${c.obj}. ${p.name} is real, but ${p.subj} is a person, not a wild animal. Still, if ${c.subj} stepped off the screen to meet ${p.name}...`
    }
    if (storybooks.length === 1) {
      const s = refer(storybooks[0])
      return `${s.Name} lives in stories and ${c.name} lives in a television show, so nobody has ever met either of them. But if the two of them met...`
    }
    return `${c.Name} lives in a television show, not in the wild — nobody has ever met ${c.obj}. But if ${c.subj} stepped off the screen today...`
  }
  if (storybooks.length === 2) return 'Neither of these is a real animal — they both live in stories. But if their two stories met...'
  if (storybooks.length === 1 && persons.length === 1) {
    const s = refer(storybooks[0])
    const p = refer(persons[0])
    return `${s.Name} lives in stories, not in the wild — nobody has ever met a real one. ${p.name} is real, but ${p.subj} is a person, not a wild animal. Still, if one stepped out of a storybook to meet ${p.obj}...`
  }
  if (storybooks.length === 1) {
    return `${refer(storybooks[0]).Name} lives in stories, not in the wild — nobody has ever met a real one. But if one stepped out of a storybook today...`
  }
  if (persons.length === 1) {
    const p = refer(persons[0])
    const animal = refer(persons[0] === a ? b : a).name.replace(/^the /, '')
    return `${p.name} is a real person, not an animal — no ${animal} has ever squared up to ${p.obj}. But if ${p.subj} walked out into the wild today...`
  }
  return 'These two never met in real life — they lived in completely different times. But if they did meet...'
}

export default { eraNote, isLegend, isStorybook, isCrossEra }
